// src/components/ArFollowUp.js
import React, { useState } from 'react';
import './ArFollowUp.css';

const ArFollowUp = () => {
  const [formData, setFormData] = useState({
    service: '',
    size: '',
    phone: '',
    email: '',
    website: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const serviceOptions = [
    'AR Follow-Up Services',
    'Medical Billing',
    'Medical Coding',
    'Credentialing',
    'Denial Management',
    'Revenue Cycle Management'
  ];

  const sizeOptions = ['Small Practice', 'Mid Practice', 'Large Practice', 'Hospital', 'Clinic'];

  const arServices = [
    { title: 'Insurance Claims Follow-Up', desc: 'We proactively follow up with insurance companies on unpaid and partially paid claims to ensure accurate and timely reimbursement.' },
    { title: 'Aging A/R Management', desc: 'Our team prioritizes claims based on aging buckets (30/60/90/120+ days) to focus on higher-risk and high-value accounts.' },
    { title: 'EDIs, ERAs & EFT Setups', desc: 'We help you process and submit EDIs, ERAs, and EFTs for faster claims submission, reducing claims expenses, and getting paid faster via direct deposit.' },
    { title: 'Denied & Underpaid Claims Resolution', desc: 'We identify denial-related A/R issues, correct errors, submit appeals, and recover underpayments based on payer contracts.' },
    { title: 'Timely Filing Marketing', desc: 'We track payer filing deadlines to prevent avoidable write-offs due to timely filing details.' },
    { title: 'Payer Escalation & Communication', desc: 'Our accounts receivable management services include direct communication with payers, documented interactions, and escalations when necessary.' }
  ];

  const commonIssues = [
    'Delay in Payments / Slow Claim processing',
    'Unpaid Insurance claims',
    'Partially paid or underpaid claims',
    'Wrongly Denied Claims by Insurance Companies',
    'Claims pending for medical review',
    'Authorization-related delays',
    'Coordination of benefits (COB) issues',
    'Secondary and tertiary insurance follow-up'
  ];

  const benefits = [
    'Improved payer accountability',
    'Fewer write-offs and revenue losses',
    'Higher collection and reimbursement rates',
    'Reduced days in Accounts Receivable',
    'Improved cash flow and revenue predictability'
  ];

  const processSteps = [
    'Claims tracking for all claims with 2+ days of aging',
    'Review and analyze outstanding claims',
    'Identify overdue claims and value',
    'Initiate payer follow-up',
    'Identify underpayments and denials',
    'Record unresolved claims',
    'Mark trends and optimize workflows'
  ];

  const whyChooseFeatures = [
    { icon: '⚡', title: 'Faster Unpaid Claims', desc: 'Tracking unpaid claims with 24/7 Aging monitoring' },
    { icon: '📈', title: 'Higher Collection Rate', desc: 'Higher collections rate with dedicated follow-up' },
    { icon: '👥', title: 'Experienced Medical AR', desc: 'Experienced medical accounts receivable provider' },
    { icon: '💬', title: 'Transparent Communication', desc: 'Transparent communication with payers' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://formspree.io/f/xrejwqgl', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Thank you! We will contact you shortly.' });
        setFormData({ service: '', size: '', phone: '', email: '', website: '' });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus({ type: '', message: '' }), 5000);
    }
  };

  return (
    <main className="ar-followup-page">
      {/* Hero Section */}
      <section className="ar-hero">
        <div className="container">
          <div className="ar-hero-content">
            <h1 className="ar-hero-title">
              Maximize Revenue with Expert <span className="highlight">AR Follow-Up Services</span>
            </h1>
            <p className="ar-hero-description">
              Turn outstanding AR into paid revenue with accounts receivable follow-up services from Providers Care Billing LLC. 
              Our results-driven AR follow-up solutions recover revenue faster, reduce delays, and streamline collections to 
              boost your practice's financial health.
            </p>
            <div className="ar-hero-badges">
              <div className="hero-badge">Promote leads & Zero Cost</div>
              <div className="hero-badge">Reduce accounts receivable cost up to 40%</div>
            </div>
            <button className="hero-appoint-btn">Schedule An Appointment</button>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="ar-intro">
        <div className="container">
          <div className="ar-intro-content">
            <h2 className="ar-intro-title">Medical Accounts Receivable (AR) Follow-Up Services</h2>
            <p className="ar-intro-text">
              Accelerate payments, reduce aging A/R, and improve cash flow. Outstanding and aging accounts receivable in 
              medical billing can significantly impact the financial stability of your practice. At Providers Care Billing LLC, 
              our professional AR follow-up services are designed to recover unpaid claims, reduce days in A/R, and improve 
              overall healthcare accounts receivable management service.
            </p>
            <p className="ar-intro-text">
              Our experienced AR follow-up specialists work diligently with insurance payers and partners to ensure timely 
              payment resolution while maintaining full compliance with payer and regulatory requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Consultation Form Section */}
      <section className="ar-consultation">
        <div className="container">
          <div className="ar-form-wrapper">
            <h2 className="ar-form-title">Get a free Consultation</h2>
            {submitStatus.message && <div className={`status-message ${submitStatus.type}`}>{submitStatus.message}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <select name="service" value={formData.service} onChange={handleChange} required>
                    <option value="">Select Service</option>
                    {serviceOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <select name="size" value={formData.size} onChange={handleChange} required>
                    <option value="">Select Size</option>
                    {sizeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><input type="tel" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} required /></div>
                <div className="form-group"><input type="email" name="email" placeholder="Email Address" value={formData.email} onChange={handleChange} required /></div>
              </div>
              <div className="form-row">
                <div className="form-group full-width"><input type="url" name="website" placeholder="Website or App" value={formData.website} onChange={handleChange} /></div>
              </div>
              <button type="submit" className="ar-submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Sending...' : 'Get Started'}</button>
            </form>
          </div>
        </div>
      </section>

      {/* Comprehensive AR Services Section */}
      <section className="ar-services">
        <div className="container">
          <div className="ar-services-header">
            <h2 className="section-title-dark">Our Comprehensive AR Follow-Up Services</h2>
            <p className="section-subtitle-dark">
              With Providers Care Billing LLC, medical billing accounts receivable follow-up services, your practice can 
              streamline reimbursement processes and cut costs. Our suite of services is designed to streamline your cash 
              flow and get your claims paid faster.
            </p>
          </div>
          <div className="ar-services-grid">
            {arServices.map((service, index) => (
              <div className="ar-service-card" key={index}>
                <h3>{service.title}</h3>
                <p>{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialty Expertise Section */}
      <section className="ar-specialty">
        <div className="container">
          <div className="ar-specialty-content">
            <h2 className="ar-specialty-title">Specialty-Specific A/R Expertise</h2>
            <p className="ar-specialty-text">
              We provide medical AR follow-up services for all specialties, with dedicated AR specialists and account managers 
              who understand specialty-specific payer rules, billing workflows, and reimbursement challenges.
            </p>
            <button className="specialty-appoint-btn">Schedule An Appointment</button>
          </div>
        </div>
      </section>

      {/* Common AR Issues Section */}
      <section className="ar-issues">
        <div className="container">
          <div className="ar-issues-header">
            <h2 className="issues-title">Common AR Issues We Resolve</h2>
            <p className="issues-subtitle">
              At Provider Care Billing LLC, our goal is not only to improve your accounts receivable (AR) follow-up but also 
              to prevent these common AR issues.
            </p>
          </div>
          <div className="issues-grid">
            {commonIssues.map((issue, index) => (
              <div className="issue-item" key={index}>
                <span className="issue-check">✓</span>
                <span>{issue}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="ar-benefits">
        <div className="container">
          <div className="benefits-wrapper">
            <h2 className="benefits-main-title">Benefits of Professional AR Follow-Up Services</h2>
            <p className="benefits-subtitle">
              By partnering with Provider Care Billing LLC for AR Follow-Up Services, our clients gain several benefits and 
              do not face any difficulties in receiving payments for their medical services.
            </p>
            <div className="benefits-grid">
              {benefits.map((benefit, index) => (
                <div className="benefit-card" key={index}>
                  <span className="benefit-check">✓</span>
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="ar-process">
        <div className="container">
          <h2 className="process-main-title">Our AR Follow-Up Process</h2>
          <p className="process-subtitle">
            Because we specialize in end-to-end revenue cycle management for healthcare providers, our AR Follow-Up process 
            is structured, proactive, and results-driven. There are several phases and features involved in the AR follow-up process.
          </p>
          <div className="process-steps-grid">
            {processSteps.map((step, index) => (
              <div className="process-step-card" key={index}>
                <span className="step-number-ar">{index + 1}</span>
                <span className="step-text-ar">{step}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="ar-why-choose">
        <div className="container">
          <h2 className="why-choose-ar-title">Why Choose Providers Care Billing LLC For AR Follow-Up?</h2>
          <p className="why-choose-ar-subtitle">
            Provider Care Billing LLC helps you track, manage, and recover every dollar you owe. There are many benefits to working with us.
          </p>
          <div className="why-choose-ar-grid">
            {whyChooseFeatures.map((feature, index) => (
              <div className="why-choose-ar-card" key={index}>
                <div className="ar-why-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </div>
            ))}
          </div>
          <div className="get-started-wrapper">
            <button className="get-started-ar-btn">Get Started With AR Follow-Up Services →</button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ArFollowUp;